"use client";

import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { DynamicIcon, type IconName } from "lucide-react/dynamic";
import Link from "next/link";

type FAQItem = {
	id: string;
	icon: IconName;
	question: string;
	answer: string;
};

export interface FAQSectionProps {
	items?: FAQItem[];
	className?: string;
}

export default function FAQSection({
	items = faqItems,
	className,
}: FAQSectionProps) {
	return (
		<section className={cn("my-12", className)}>
			<div className="mx-auto max-w-5xl">
				<div className="flex flex-col gap-10 md:flex-row md:gap-16">
					<div className="md:w-1/3">
						<div className="sticky top-20 text-center md:text-start">
							<h2 className="mt-4 text-3xl font-bold">
								Frequently Asked Questions
							</h2>
							<p className="text-muted-foreground mt-4">
								Can&apos;t find what you&apos;re looking for? Contact our{" "}
								<Link
									href="#"
									className="text-primary font-medium hover:underline"
								>
									customer support team
								</Link>
							</p>
						</div>
					</div>
					<div className="md:w-2/3">
						<Accordion type="single" collapsible className="w-full space-y-2">
							{items.map((item) => (
								<AccordionItem
									key={item.id}
									value={item.id}
									className="bg-background shadow-xs rounded-lg border px-4 last:border-b"
								>
									<AccordionTrigger className="cursor-pointer items-center py-5 hover:no-underline">
										<div className="flex items-center gap-3">
											<div className="flex size-6">
												<DynamicIcon
													name={item.icon}
													className="m-auto size-4"
												/>
											</div>
											<span className="text-sm">{item.question}</span>
										</div>
									</AccordionTrigger>
									<AccordionContent className="pb-5">
										<div className="px-9">
											<p className="text-sm">{item.answer}</p>
										</div>
									</AccordionContent>
								</AccordionItem>
							))}
						</Accordion>
					</div>
				</div>
			</div>
		</section>
	);
}

const faqItems: FAQItem[] = [
	{
		id: "item-1",
		icon: "clock",
		question: "What are your business hours?",
		answer:
			"Our customer service team is available Monday through Friday from 9:00 AM to 8:00 PM EST, and weekends from 10:00 AM to 6:00 PM EST. During holidays, hours may vary and will be posted on our website.",
	},
	{
		id: "item-2",
		icon: "credit-card",
		question: "How do subscription payments work?",
		answer:
			"Subscription payments are automatically charged to your default payment method on the same day each month or year, depending on your billing cycle. You can update your payment information and view billing history in your account dashboard.",
	},
	{
		id: "item-3",
		icon: "truck",
		question: "Can I get a new live map?",
		answer: "Yes, we offer several expedited integration options at checkout.",
	},
	{
		id: "item-4",
		icon: "globe",
		question: "Do you offer localized support?",
		answer:
			"We offer multilingual support in English, Hindi. Our support team can assist customers in these languages via email, chat, and phone during standard business hours for each respective region.",
	},
	{
		id: "item-5",
		icon: "package",
		question: "How do I get a new live map?",
		answer:
			"Once you purchase a new live map, you'll receive an email with instructions on how to activate it.",
	},
];
